import React, { useRef, useMemo } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import contentImageService from '../../services/contentImageService';
import { getFileUrl } from '../../utils/urlUtils';
import styles from './RichTextEditor.module.css';

// Import TinyMCE core and required plugins (self-hosted)
import 'tinymce/tinymce';
import 'tinymce/models/dom';
import 'tinymce/themes/silver';
import 'tinymce/icons/default';

// Import plugins
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/image';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/code';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/insertdatetime';
import 'tinymce/plugins/media';
import 'tinymce/plugins/table';
import 'tinymce/plugins/wordcount';
import 'tinymce/plugins/quickbars';
import 'tinymce/plugins/pagebreak';

// Import skin and content CSS
import 'tinymce/skins/ui/oxide/skin.min.css';

/**
 * Universal Rich Text Editor component using TinyMCE
 * Features:
 * - Character count with limit enforcement
 * - Configurable toolbar (compact/full)
 * - Image upload with automatic tracking
 * - Image resize and alignment (Word-like)
 * - Responsive design
 * - Accessibility support
 * 
 * @param {Object} props
 * @param {string} props.value - HTML content
 * @param {Function} props.onChange - Callback when content changes (html: string)
 * @param {number} props.maxLength - Maximum character count (default: 5000)
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.disabled - Whether the editor is disabled
 * @param {boolean} props.compactToolbar - Use compact toolbar (fewer options)
 * @param {number} props.height - Preferred editor height in pixels
 * @param {number} props.minHeight - Minimum editor height in pixels
 * @param {number} props.maxHeight - Maximum editor height in pixels
 * @param {string} props.className - Additional CSS class
 * @param {string} props.parentType - Entity type for image tracking (e.g., "Event", "Survey")
 * @param {number} props.parentId - Entity ID for image tracking (null for new entities)
 */
const RichTextEditor = ({
  value = '',
  onChange,
  maxLength = 5000,
  placeholder = '',
  disabled = false,
  compactToolbar = false,
  height = 700,
  minHeight = 400,
  maxHeight = 1000,
  className = '',
  parentType = null,
  parentId = null,
}) => {
  const { t } = useTranslation();
  const editorRef = useRef(null);
  const uploadingRef = useRef(false);

  // Calculate plain text length for character count
  const textLength = useMemo(() => {
    if (!value) return 0;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = value;
    return (tempDiv.textContent || tempDiv.innerText || '').length;
  }, [value]);

  const isOverLimit = textLength > maxLength;
  const remainingChars = maxLength - textLength;

  // Custom image upload handler
  const imageUploadHandler = async (blobInfo, progress) => {
    if (!parentType) {
      throw new Error(t('richTextEditor.imageUploadNotEnabled', 'Image upload is not enabled for this editor'));
    }

    if (uploadingRef.current) {
      throw new Error(t('richTextEditor.uploadInProgress', 'An upload is already in progress'));
    }

    const file = blobInfo.blob();
    
    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error(t('richTextEditor.fileTooLarge', 'Image is too large. Maximum size is 10MB.'));
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error(t('richTextEditor.invalidFileType', 'Only image files are allowed.'));
    }

    uploadingRef.current = true;

    try {
      progress(30);
      const result = await contentImageService.upload(file, parentType);
      progress(100);
      
      // Get the URL - handle both camelCase (url) and PascalCase (Url) from API
      const relativeUrl = result.url || result.Url;
      
      if (!relativeUrl) {
        throw new Error('No URL returned from upload');
      }
      
      // Convert to full URL for proper display
      const imageUrl = getFileUrl(relativeUrl) || relativeUrl;
      
      return imageUrl;
    } catch (error) {
      console.error('Failed to upload image:', error);
      const errorMessage = error.response?.data?.message || 
        error.response?.data?.detail || 
        error.message ||
        t('richTextEditor.uploadFailed', 'Failed to upload image. Please try again.');
      throw new Error(errorMessage);
    } finally {
      uploadingRef.current = false;
    }
  };

  // Configure toolbar based on compactToolbar prop
  const toolbar = compactToolbar
    ? 'bold italic underline | bullist numlist | link' + (parentType ? ' image' : '') + ' | removeformat'
    : 'blocks | bold italic underline strikethrough | alignleft aligncenter alignright | bullist numlist | outdent indent | link' + 
      (parentType ? ' image' : '') + ' | forecolor backcolor | pagebreak | removeformat';

  // Configure plugins
  const plugins = [
    'advlist',
    'autolink', 
    'lists',
    'link',
    ...(parentType ? ['image', 'quickbars'] : []),
    'charmap',
    'preview',
    'searchreplace',
    'visualblocks',
    'wordcount',
    'pagebreak',
  ];

  const handleEditorChange = (content) => {
    if (onChange) {
      // Get plain text length
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      const plainTextLength = (tempDiv.textContent || tempDiv.innerText || '').length;
      
      // Only update if within limit or user is deleting
      if (plainTextLength <= maxLength || plainTextLength < textLength) {
        onChange(content);
      }
    }
  };

  return (
    <div className={`${styles.editorContainer} ${className} ${disabled ? styles.disabled : ''}`}>
      <div className={`${styles.editorWrapper} ${isOverLimit ? styles.overLimit : ''}`}>
        <Editor
          onInit={(evt, editor) => {
            editorRef.current = editor;
          }}
          value={value || ''}
          onEditorChange={handleEditorChange}
          disabled={disabled}
          init={{
            height,
            min_height: minHeight,
            max_height: maxHeight,
            menubar: false,
            plugins: plugins,
            toolbar: toolbar,
            placeholder: placeholder,
            
            // Image configuration
            image_advtab: true,
            image_caption: true,
            automatic_uploads: true,
            images_upload_handler: parentType ? imageUploadHandler : undefined,
            file_picker_types: 'image',
            image_dimensions: true,
            
            // URL handling - keep URLs as-is (root-relative) for backend reconciliation
            convert_urls: false,
            
            // Image resize settings (Word-like behavior)
            object_resizing: 'img',
            resize_img_proportional: true,
            
            // Image toolbar (appears when clicking an image)
            quickbars_insert_toolbar: false,
            quickbars_selection_toolbar: 'bold italic | link',
            quickbars_image_toolbar: 'alignleft aligncenter alignright | image',
            
            // Content styling
            content_style: `
              body { 
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                font-size: 14px;
                line-height: 1.6;
                color: #333;
                padding: 12px;
                position: relative;
              }
              img {
                max-width: 100%;
                height: auto;
                cursor: pointer;
              }
              img:hover {
                outline: 2px solid #1890ff;
              }
              img[data-mce-selected] {
                outline: 2px solid #1890ff !important;
              }
              img.align-left {
                float: left;
                margin: 0 15px 15px 0;
              }
              img.align-right {
                float: right;
                margin: 0 0 15px 15px;
              }
              img.align-center {
                display: block;
                margin: 15px auto;
              }
              /* Page break styling */
              .mce-pagebreak {
                border: 1px dashed #ccc;
                margin: 20px 0;
                page-break-before: always;
              }
              /* Resize handle styling - position: absolute is required for handles to work */
              .mce-resizehandle {
                position: absolute;
                width: 10px;
                height: 10px;
                background-color: #1890ff;
                border: 1px solid #fff;
                box-sizing: border-box;
                z-index: 10000;
              }
              /* Fix resize ghost/preview positioning */
              .mce-clonedresizable {
                position: absolute !important;
                opacity: 0.5;
                z-index: 9999;
              }
            `,
            
            // Paste settings
            paste_data_images: parentType ? true : false,
            
            // Branding
            branding: false,
            promotion: false,
            
            // License key (free tier - no key needed for self-hosted)
            license_key: 'gpl',
            
            // Skin
            skin: false, // We import skin CSS directly
            content_css: false, // We use content_style instead
            
            // Setup callback for additional configuration
            setup: (editor) => {
              // Double-click on image opens properties
              editor.on('dblclick', (e) => {
                if (e.target.nodeName === 'IMG') {
                  editor.execCommand('mceImage');
                }
              });
              
              // Handle paste of images
              if (parentType) {
                editor.on('paste', async (e) => {
                  const items = e.clipboardData?.items;
                  if (!items) return;
                  
                  for (let i = 0; i < items.length; i++) {
                    if (items[i].type.indexOf('image') !== -1) {
                      const file = items[i].getAsFile();
                      if (file) {
                        e.preventDefault();
                        const loadingToast = toast.loading(
                          t('richTextEditor.uploadingImage', 'Uploading image...')
                        );
                        
                        try {
                          const result = await contentImageService.upload(file, parentType);
                          const relativeUrl = result.url || result.Url;
                          
                          if (relativeUrl) {
                            const imageUrl = getFileUrl(relativeUrl) || relativeUrl;
                            editor.insertContent(`<img src="${imageUrl}" />`);
                            toast.update(loadingToast, {
                              render: t('richTextEditor.imageUploaded', 'Image uploaded successfully'),
                              type: 'success',
                              isLoading: false,
                              autoClose: 2000,
                            });
                          }
                        } catch (error) {
                          toast.update(loadingToast, {
                            render: t('richTextEditor.uploadFailed', 'Failed to upload image. Please try again.'),
                            type: 'error',
                            isLoading: false,
                            autoClose: 5000,
                          });
                        }
                        break;
                      }
                    }
                  }
                });
              }
            },
          }}
        />
      </div>
      <div className={`${styles.footer} ${isOverLimit ? styles.overLimit : ''}`}>
        <span className={styles.charCount}>
          {textLength.toLocaleString()} / {maxLength.toLocaleString()} {t('common.characters', 'characters')}
          {isOverLimit && (
            <span className={styles.overLimitWarning}>
              {' '}({Math.abs(remainingChars).toLocaleString()} {t('common.overLimit', 'over limit')})
            </span>
          )}
        </span>
      </div>
    </div>
  );
};

export default RichTextEditor;
